import { type MouseEvent, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Badge, Icon, IconButton } from '@socialchorus/shared-ui-components';
import Spinner from '../../../../components/ui/spinner';
import type { ContentVideo } from '../../../../models/user-generated-contents/types';
import { MAX_VIDEOS } from './use-video-uploader';
import { useSortableItem, SortableGrid } from './sortable';
import './videos.scss';

type VideoItemProps = {
  video: ContentVideo;
  index: number;
  onDelete: (index: number) => void;
};

const SortableVideoItem = ({ video, index, onDelete }: VideoItemProps) => {
  const { t } = useTranslation();
  const { setNodeRef, style, attributes, listeners } = useSortableItem(
    video.url ?? `video-${index}`
  );
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  // 'submitted'/'progressing' means the video is still transcoding, so its playable
  // url/hls_url are not ready yet; used to hide the play button until it can play.
  const isProcessing =
    video.status === 'submitted' || video.status === 'progressing';

  const renderThumbnail = () => {
    // Show the preview image whenever we have one. While a video is still processing its
    // playable `url` is null and the raw `transcode_url` is a private presigned S3
    // URL the browser can't load, so a <video> element renders as an empty (dark)
    // box, the "black thumbnail" from FE-35717. A duplicate's videos carry the
    // source's preview_image_url, so the preview image is available immediately.
    if (video.preview_image_url) {
      return (
        <img
          className="ugc-videos__thumbnail"
          src={video.preview_image_url}
          alt={t('user_generated_content.media.videos.video_thumbnail', {
            index: index + 1,
          })}
          draggable={false}
        />
      );
    }

    // No preview image: fall back to the video's first frame (unchanged behavior; a
    // still-processing video with no preview image shows the same as before).
    return (
      // eslint-disable-next-line jsx-a11y/media-has-caption
      <video
        className="ugc-videos__thumbnail"
        src={`${video.url}#t=0.1`}
        preload="metadata"
        muted
        draggable={false}
        aria-label={t('user_generated_content.media.videos.video_thumbnail', {
          index: index + 1,
        })}
      />
    );
  };

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        className="ugc-videos__item"
        {...attributes}
        {...listeners}
      >
        {renderThumbnail()}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          className="ugc-videos__item-overlay"
          onKeyDown={(e) => e.stopPropagation()}
        >
          {/* Play only once the video is out of transcoding: the lightbox plays
              video.url / hls_url, which are null while still processing. */}
          {!isProcessing && (
            <IconButton
              iconName="play_circle"
              size="compact"
              className="bg-black-05"
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                setIsLightboxOpen(true);
              }}
              aria-label={t('user_generated_content.media.videos.play')}
            />
          )}
          <IconButton
            iconName="delete"
            size="compact"
            className="bg-black-05"
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              onDelete(index);
            }}
            aria-label={t('user_generated_content.media.videos.delete')}
          />
        </div>
      </div>
      {isLightboxOpen &&
        createPortal(
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
          <div
            className="ugc-videos__lightbox-overlay"
            onClick={() => setIsLightboxOpen(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setIsLightboxOpen(false);
            }}
          >
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div
              className="ugc-videos__lightbox-content"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video className="ugc-videos__lightbox-player" controls autoPlay>
                {video.hls_url && (
                  <source src={video.hls_url} type="application/x-mpegURL" />
                )}
                <source src={video.url} />
              </video>
            </div>
            <IconButton
              iconName="close"
              size="compact"
              className="ugc-videos__lightbox-close"
              onClick={() => setIsLightboxOpen(false)}
              aria-label={t('user_generated_content.media.videos.close')}
            />
          </div>,
          document.body
        )}
    </>
  );
};

type VideosProps = {
  videos: ContentVideo[];
  inProgress: File[];
  totalCount: number;
  // When true, the counter renders in its error state (limit exceeded).
  limitError?: boolean;
  onDelete: (index: number) => void;
  onReorder: (videos: ContentVideo[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dragHandleProps?: Record<string, any>;
  // True while this group is being dragged / keyboard-selected
  isDragging?: boolean;
};

const Videos = ({
  videos,
  inProgress,
  totalCount,
  limitError,
  onDelete,
  onReorder,
  dragHandleProps,
  isDragging,
}: VideosProps) => {
  const { t } = useTranslation();

  return (
    <div className="ugc-videos__section">
      <div
        className={`ugc-videos__section-header${
          dragHandleProps ? ' ugc-videos__section-header--draggable' : ''
        }${isDragging ? ' ugc-videos__section-header--dragging' : ''}`}
        {...dragHandleProps}
      >
        {dragHandleProps && (
          <Icon
            className={`ugc-videos__drag-handle${
              isDragging ? ' ugc-videos__drag-handle--active' : ''
            }`}
            size={20}
            aria-hidden
          >
            drag_indicator
          </Icon>
        )}
        <Badge
          color={limitError ? 'negative' : 'default'}
          text={t('user_generated_content.media.videos.section_label', {
            count: totalCount,
            max: MAX_VIDEOS,
          })}
        />
      </div>
      <SortableGrid<ContentVideo>
        items={videos}
        getId={(vid) => vid.url}
        onReorder={(reordered) =>
          onReorder(
            reordered.map((vid: ContentVideo, i: number) => ({
              ...vid,
              order: i,
            }))
          )
        }
      >
        <div className="ugc-videos__grid">
          {videos.map((video, i) => (
            <SortableVideoItem
              key={video.url}
              video={video}
              index={i}
              onDelete={onDelete}
            />
          ))}
          {inProgress.map((file) => (
            <div
              key={`${file.name}-${file.lastModified}`}
              className="ugc-videos__item ugc-videos__item--uploading"
            >
              <Spinner loading />
              <span className="text-caption">{t('common.uploading')}</span>
            </div>
          ))}
        </div>
      </SortableGrid>
    </div>
  );
};

export default Videos;
